import React from 'react';
import { Container, Typography, Box, Divider, Stack } from '@mui/material';
import { 
  MonetizationOn, 
  ShoppingCart, 
  ShowChart, 
  BusinessCenter, 
  AccountTree, 
  CurrencyBitcoin,
  Layers,
  Speed,
  Security,
  CloudDone,
  SportsEsports
} from '@mui/icons-material';
import { FadeInSection } from '../Animations/FadeInAnimation';

export const IndustrySection: React.FC = () => {
  const industries = [
    {
      title: 'FinTech Solutions',
      description: 'Secure and scalable financial technology solutions with robust payment integrations and regulatory compliance.',
      icon: <MonetizationOn sx={{ fontSize: 40 }} />,
      features: ['Payment Systems', 'Banking Solutions', 'Financial Analytics']
    },
    {
      title: 'E-Commerce',
      description: 'End-to-end e-commerce platforms with seamless shopping experiences and comprehensive inventory management.',
      icon: <ShoppingCart sx={{ fontSize: 40 }} />,
      features: ['Custom Marketplaces', 'Inventory Systems', 'Payment Integration']
    },
    {
      title: 'Game Development',
      description: 'Games with immersive gameplay, multiplayer functionality, and optimized performance.',
      icon: <SportsEsports sx={{ fontSize: 40 }} />,
      features: ['WebGL & Three.js', 'Multiplayer Systems', 'Physics Engines']
    },
    {
      title: 'Algorithmic Trading',
      description: 'High-performance trading systems with real-time data processing and automated strategy execution.',
      icon: <ShowChart sx={{ fontSize: 40 }} />,
      features: ['Strategy Automation', 'Real-time Analysis', 'Risk Management']
    },
    {
      title: 'Trading Platforms',
      description: 'Custom trading platforms with advanced charting, order management, and portfolio tracking capabilities.',
      icon: <ShowChart sx={{ fontSize: 40 }} />,
      features: ['Advanced Charts', 'Order Management', 'Portfolio Tracking']
    },
    {
      title: 'CRM & ERP',
      description: 'Tailored business management solutions that streamline operations and enhance customer relationships.',
      icon: <BusinessCenter sx={{ fontSize: 40 }} />,
      features: ['Customer Management', 'Resource Planning', 'Business Analytics']
    },
    {
      title: 'Crypto & Blockchain',
      description: 'Innovative blockchain solutions and cryptocurrency platforms with robust security measures.',
      icon: <CurrencyBitcoin sx={{ fontSize: 40 }} />,
      features: ['Wallet Integration', 'Smart Contracts', 'DeFi Solutions']
    }
  ];

  const productFeatures = [
    {
      icon: <Layers />,
      title: 'Scalable Architecture',
      description: 'Built to grow with your business needs'
    },
    {
      icon: <Speed />,
      title: 'High Performance',
      description: 'Optimized for speed and efficiency'
    },
    {
      icon: <Security />,
      title: 'Enterprise Security',
      description: 'Built with security at its core'
    },
    {
      icon: <CloudDone />,
      title: 'Cloud-Native',
      description: 'Modern cloud infrastructure'
    }
  ];

  return (
    <Box sx={{ py: 8, bgcolor: 'background.default' }}>
      <Container maxWidth="lg">
        {/* Custom Product Development Section */}
        <Box sx={{ mb: 10 }}>
          <Typography
            component="h2"
            variant="h3"
            align="center"
            color="text.primary"
            gutterBottom
          >
            Custom Product Development
          </Typography>
          <Typography
            variant="h6"
            align="center"
            color="text.secondary"
            paragraph
            sx={{ mb: 6 }}
          >
            Turn your vision into reality with our comprehensive product development expertise
          </Typography>
          <FadeInSection delay={0.6}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={4}
              useFlexGap
              flexWrap="wrap"
              justifyContent="center"
            >
              {productFeatures.map((feature, index) => (
                
                <Box
                  key={index}
                  sx={{
                    p: 3,
                    width: { xs: '100%', sm: 'calc(50% - 16px)', lg: 'calc(25% - 24px)' },
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    textAlign: 'center',
                    transition: 'transform 0.3s',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 3
                    }
                  }}
                >
                  <Box sx={{ color: 'primary.main', mb: 2, '& > svg': { fontSize: 40 } }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography color="text.secondary">
                    {feature.description}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </FadeInSection>
        </Box>

        <Divider sx={{ my: 8 }} />

        {/* Industry Expertise Section */}
        <FadeInSection delay={0.6}>
          <Typography
            component="h2"
            variant="h3"
            align="center"
            color="text.primary"
            gutterBottom
          >
            Industry Expertise
          </Typography>
          <Typography
            variant="h6"
            align="center"
            color="text.secondary"
            paragraph
            sx={{ mb: 6 }}
          >
            Transforming businesses with domain-specific solutions and cutting-edge technology
          </Typography>
        </FadeInSection>
        <FadeInSection delay={0.8}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={4}
            useFlexGap
            flexWrap="wrap"
            justifyContent="center"
          >
            {industries.map((industry) => (
              <Box
                key={industry.title}
                sx={{
                  width: { xs: '100%', md: 'calc(50% - 16px)', lg: 'calc(33.333% - 24px)' },
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 3,
                    '& .arrow-icon': {
                      transform: 'translateX(4px)'
                    }
                  },
                  overflow: 'hidden'
                }}
              >
                <Box
                  sx={{
                    p: 3,
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                    <Box
                      sx={{
                        color: 'primary.main',
                        bgcolor: 'primary.lighter',
                        p: 1,
                        borderRadius: 2
                      }}
                    >
                      {industry.icon}
                    </Box>
                    <Typography variant="h5" component="h3">
                      {industry.title}
                    </Typography>
                  </Stack>
                  <Typography color="text.secondary" sx={{ mb: 2 }}>
                    {industry.description}
                  </Typography>
                  <Stack spacing={1}>
                    {industry.features.map((feature, index) => (
                      <Stack
                        key={index}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                      >
                        <AccountTree
                          sx={{
                            fontSize: 16,
                            color: 'primary.main'
                          }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {feature}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
                {/* <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    bgcolor: 'background.default'
                  }}
                >
                  <Button
                    endIcon={
                      <ArrowForward className="arrow-icon" sx={{ transition: 'transform 0.2s' }} />
                    }
                    sx={{ textTransform: 'none' }}
                  >
                    Learn More
                  </Button>
                </Box> */}
              </Box>
            ))}
          </Stack>
        </FadeInSection>
      </Container>
    </Box>
  );
};

